import { useState } from 'react';
import { Mail } from 'lucide-react';
import Footer from '../components/layout/footer';
import { useLanguage } from '../context/LanguageContext';

const CONTACT_EMAIL = 'contact@maisonadji.com';

export default function ContactPage() {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    // Simulation d'envoi — à remplacer par Resend plus tard
    await new Promise((r) => setTimeout(r, 1200));
    setStatus('sent');
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-6 text-center border-b border-white/5">
        <p className="font-['Sora'] text-xs tracking-[0.35em] text-white/40 uppercase mb-3">
          Maison Adji
        </p>
        <h1 className="font-['Sora'] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
          {t('contact.title')}
        </h1>
        <p className="mt-4 font-['Rubik'] text-white/60 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
          {t('contact.subtitle')}
        </p>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Bloc email de contact */}
        <div className="mb-12 md:mb-16 p-6 md:p-8 rounded-2xl bg-white/5 border border-white/10">
          <p className="font-['Rubik'] text-xs tracking-widest uppercase text-white/40 mb-2">
            {t('contact.email_label')}
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-3 font-['Sora'] text-lg md:text-xl text-white hover:text-white/80 transition-colors"
          >
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white/80">
              <Mail className="w-5 h-5" />
            </span>
            {CONTACT_EMAIL}
          </a>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label className="block">
              <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">
                {t('contact.name')}
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('contact.placeholder_name')}
                required
                className="w-full px-4 py-3.5 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl placeholder:text-white/30 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors"
              />
            </label>
            <label className="block">
              <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">
                {t('contact.email')}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('contact.placeholder_email')}
                required
                className="w-full px-4 py-3.5 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl placeholder:text-white/30 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors"
              />
            </label>
          </div>
          <label className="block">
            <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">
              {t('contact.subject')}
            </span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('contact.placeholder_subject')}
              required
              className="w-full px-4 py-3.5 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl placeholder:text-white/30 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors"
            />
          </label>
          <label className="block">
            <span className="block font-['Rubik'] text-xs tracking-widest uppercase text-white/50 mb-2">
              {t('contact.message')}
            </span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('contact.placeholder_message')}
              required
              rows={5}
              className="w-full px-4 py-3.5 font-['Rubik'] text-white bg-white/5 border border-white/10 rounded-xl placeholder:text-white/30 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors resize-y min-h-[140px]"
            />
          </label>
          {status === 'sent' && (
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-['Rubik'] text-sm">
              <p className="font-medium">{t('contact.sent')}</p>
              <p className="mt-1 text-white/70">{t('contact.sent_help')}</p>
            </div>
          )}
          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full md:w-auto min-w-[200px] py-4 px-8 font-['Rubik'] text-xs tracking-[0.2em] uppercase text-white bg-white/10 border border-white/20 rounded-xl hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'sending' ? t('contact.sending') : t('contact.send')}
          </button>
        </form>
      </div>

      <Footer />
    </div>
  );
}
